package com.exura.pages.login

import androidx.compose.runtime.*
import com.exura.components.buttons.AsyncButton
import com.exura.components.icons.UntiledIcon
import com.exura.components.inputs.InlineTextInputWithError
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraBodySizeTextVariant
import com.exura.components.pageTypography.ExuraEmphasisedTextVariant
import com.exura.components.pageTypography.ExuraHeroTextVariant
import com.exura.components.pageTypography.ExuraLinkTextVariant
import com.exura.components.pageTypography.ExuraPoppinsVariant
import com.exura.components.pageTypography.ExuraSecondaryTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.snackbar.Snackbar
import com.exura.components.snackbar.SnackbarMessage
import com.exura.core.domain.SimpleEvent
import com.exura.features.localization.commonLocalization
import com.exura.features.newauth.domain.manager.AuthManager
import com.exura.features.newauth.presentation.HandleSession
import com.exura.navigation.Route
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.koin.core.context.GlobalContext.get

@Page(Route.LOGIN)
@Layout(".components.layouts.NotLoggedInLayout")
@Composable
internal fun LoginPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()
    val authManager: AuthManager = remember { get().get() }

    val controller = rememberLoginController(context, authManager)
    val state = controller.uiState

    val event: SimpleEvent? by authManager.event.collectAsState(null)

    when (val currentEvent = event) {
        is SimpleEvent.Error -> Snackbar(
            SnackbarMessage(currentEvent.message ?: commonLocalization.current.errors.unknownErrorOccurred)
        )
        SimpleEvent.Success,
        null -> Unit
    }

    HandleSession(
        context = context,
        loggedInDestination = Route.DASHBOARD,
    )

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(Dimens.loggingBoxMinHeight),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap)
    ) {
        Header()

        Inputs(
            email = state.email,
            emailError = state.emailError,
            onEmailChange = { scope.launch { controller.handleEvent(LoginEvent.EmailChanged(it)) } },
            password = state.password,
            passwordError = state.passwordError,
            onPasswordChange = { scope.launch { controller.handleEvent(LoginEvent.PasswordChanged(it)) } },
            enabled = !state.isLoading,
        )

        LoginButton(onClick = suspend { controller.handleEvent(LoginEvent.LoginClicked) })
        PasswordHelp(onClick = { scope.launch { controller.handleEvent(LoginEvent.ForgotPasswordClicked) } })

        Spacer()

        Footer(onClick = { scope.launch { controller.handleEvent(LoginEvent.NavigateToRegister) } })
    }
}

@Composable
private fun Header() {
    Box(
        modifier = ExuraTextStyle
            .toModifier(ExuraHeroTextVariant, ExuraEmphasisedTextVariant)
            .width(60.percent)
            .textAlign(TextAlign.Center),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(loginLocalization.current.pageTitle)
    }
}

@Composable
private fun Inputs(
    email: String,
    emailError: String,
    password: String,
    passwordError: String,
    onEmailChange: (String) -> Unit,
    onPasswordChange: (String) -> Unit,
    enabled: Boolean,
) {
    Column(
        modifier = ExuraTextStyle
            .toModifier(ExuraBodyHeaderTextVariant)
            .fillMaxWidth()
            .padding(topBottom = Dimens.loggingBoxContentGap),
        verticalArrangement = Arrangement.spacedBy(Dimens.loggingBoxContentGap),
    ) {
        InlineTextInputWithError(
            value = email,
            error = emailError,
            onValueChange = onEmailChange,
            isEnabled = enabled,
            placeholderText = loginLocalization.current.fields.emailAddress,
            modifier = Modifier.fillMaxWidth(),
        )
        PasswordField(
            password = password,
            error = passwordError,
            onTextChange = onPasswordChange,
            enabled = enabled,
        )
    }
}

@Composable
internal fun PasswordField(
    password: String,
    onTextChange: (String) -> Unit,
    error: String,
    enabled: Boolean = true,
) {
    val palette = ColorMode.current.toPalette()
    var passwordVisible by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(palette.border)
            .padding(left = Dimens.inputIndent)
            .borderRadius(Dimens.inputBorderRadius)
            .fontSize(Dimens.inputDefaultFontSize),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.inputIndent),
    ) {
        UntiledIcon(
            iconSrc = if (passwordVisible) Assets.IconsUntiled.EYE_OPEN else Assets.IconsUntiled.EYE_CLOSE,
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { passwordVisible = !passwordVisible },
        )
        InlineTextInputWithError(
            value = password,
            error = error,
            onValueChange = onTextChange,
            isEnabled = enabled,
            placeholderText = loginLocalization.current.fields.password,
            inputType = if (passwordVisible) InputType.Text else InputType.Password,
            modifier = Modifier
                .weight(1f)
                .borderRadius(
                    topLeft = Dimens.spacingNone,
                    bottomLeft = Dimens.spacingNone,
                    topRight = Dimens.inputBorderRadius,
                    bottomRight = Dimens.inputBorderRadius,
                ),
        )
    }
}

@Composable
private fun LoginButton(
    onClick: suspend () -> Unit,
) {
    AsyncButton(
        text = loginLocalization.current.actions.login,
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .fontSize(Dimens.loggingBoxInputsFontSize)
            .height(1.75 * Dimens.buttonHeight)
    )
}

@Composable
private fun PasswordHelp(
    onClick: () -> Unit,
) {
    Row(
        modifier = ExuraTextStyle
            .toModifier(
                ExuraPoppinsVariant,
                ExuraSecondaryTextVariant,
                ExuraBodySizeTextVariant,
            ),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.inlineTextRelGapSize),
    ) {
        SpanText(
            text = loginLocalization.current.messages.cannotLogin,
        )

        SpanText(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { onClick() },
            text = loginLocalization.current.messages.writeToUs
        )
    }
}

@Composable
private fun Footer(
    onClick: () -> Unit,
) {
    Row(
        modifier = ExuraTextStyle
            .toModifier(ExuraPoppinsVariant, ExuraBodySizeTextVariant),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.inlineTextRelGapSize),
    ) {
        SpanText(
            text = loginLocalization.current.messages.wantToStartWithUs
        )

        SpanText(
            modifier = ExuraTextStyle
                .toModifier(ExuraPoppinsVariant, ExuraLinkTextVariant)
                .onClick { onClick() },
            text = loginLocalization.current.actions.createAccount
        )
    }
}
